/**
 * Cognito Hosted UI PKCE auth — customer (seller/buyer) pool.
 * PKCE core lives in @siliconx/shared/auth (RUF-393); this file wires it with
 * the customer pool config (ID-token, not access-token) and keeps the
 * customer-only DB-derived org identity layer, which is genuinely app-specific
 * and not part of the shared PKCE seam.
 */
import { createAuthClient, decodeJwtPayload } from '@siliconx/shared/auth';

const client = createAuthClient({
  tokenKind: 'id',
  storageKeyPrefix: 'sx_customer',
  cognitoDomain: import.meta.env.VITE_CLIENT_COGNITO_DOMAIN as string,
  clientId: import.meta.env.VITE_CLIENT_COGNITO_CLIENT_ID as string,
  redirectUri: import.meta.env.VITE_CLIENT_COGNITO_REDIRECT_URI as string,
});

export const {
  buildLoginUrl,
  exchangeCodeForTokens,
  refreshAccessToken,
  getAccessToken,
  getIdToken,
  getRefreshToken,
  isTokenExpired,
  clearTokens,
  silentRefreshOrRedirect,
  stashReturnTo,
  consumeReturnTo,
} = client;

export { client };

export function signOut(): void {
  // clearClientIdentity is declared later in this file (function hoisting).
  client.signOut(clearClientIdentity);
}

/** Decode `email` claim from the ID token — customer-specific, not part of the shared PKCE core. */
export function getEmail(idToken: string): string | null {
  const payload = decodeJwtPayload(idToken) as { email?: string };
  return payload.email ?? null;
}

/** SIL-1529: decode `sub` from the ID token - hashed into the GA4 user_id, never sent raw. */
export function getSub(idToken: string): string | null {
  const payload = decodeJwtPayload(idToken) as { sub?: string };
  return payload.sub ?? null;
}

/**
 * Return a non-expired ID token, silently refreshing first if it's missing or
 * expired. Used by API callers so a long-lived view (e.g. the onboarding wizard,
 * a single route with no navigation) doesn't submit with a stale token → 401.
 *
 * Refresh can be a no-op (e.g. a fresh page load with no in-memory refresh
 * token, since it's never persisted to storage) — in that case getIdToken()
 * still returns the same stale token that failed the check above. Re-validate
 * post-refresh so a no-op or failed refresh yields null, not a stale token.
 */
export async function getValidIdToken(): Promise<string | null> {
  const token = getIdToken();
  if (token && !isTokenExpired(token)) return token;
  await refreshAccessToken();
  const refreshed = getIdToken();
  return refreshed && !isTokenExpired(refreshed) ? refreshed : null;
}

// ---------------------------------------------------------------------------
// Org identity — sourced from the DB via GET /api/client/me, NOT JWT claims.
// The customer Cognito pool defines no custom:org* attributes, and org identity
// is authorization context that belongs in the DB: the API re-derives and
// enforces it from client_user on every request. We fetch it once and memoize
// for the page session; call clearClientIdentity() on sign-out or after the
// onboarding state changes.
// ---------------------------------------------------------------------------

export interface ClientIdentity {
  orgType: 'seller' | 'buyer' | null;
  orgId: string | null;
  orgName: string | null;
  orgRole: 'admin' | 'member' | null;
  email: string | null;
  /** @deprecated use canSell — presence-only, does not reflect completeness/active state */
  hasSellerProfile: boolean;
  /** @deprecated use canBuy — presence-only, does not reflect completeness/active state */
  hasBuyerProfile: boolean;
  onboardingCompleted: boolean;
  // RUF-380: org logo (customer portal only) + the member's team (null for admins).
  logoUrl: string | null;
  teamId: string | null;
  // RUF-539/RUF-540: capability flags — true iff the profile exists, is active,
  // and is complete. Drives nav/route/action gating in place of hasSellerProfile
  // /hasBuyerProfile (which only reflect presence).
  canSell: boolean;
  canBuy: boolean;
  /** Machine-readable keys from the shared profileCompleteness() predicate — map to labels in the portal. */
  sellerMissingFields: string[];
  buyerMissingFields: string[];
  /**
   * SIL-993: 'business' (default) or 'individual' (customer_org.organization_type,
   * 861). Drives whether the portal opens the individual profile-completion
   * flow on first sign-in and which profile editor /profile shows.
   */
  organizationType: 'business' | 'individual' | null;
  /** SIL-993: individual_profile missing-field keys. Always [] for a business org. */
  individualProfileMissing: string[];
  /**
   * SIL-1007/SIL-1008: true iff a tos_acceptance row exists for this user at
   * the current TOS_VERSION (packages/shared/src/dto/tos.ts). Drives the
   * customer-portal ToS clickwrap gate (TosGate, __root.tsx) — false blocks
   * every other route until POST /api/client/tos/accept succeeds.
   */
  tosAccepted: boolean;
  /**
   * SIL-994: single org-identity label — legalName for a business, "First
   * Last" (individual_profile) for an individual. Consume this instead of
   * orgName for anything showing "who is this" (portal shell). Never put it
   * in document.title - analytics send the title with every hit (SIL-1529).
   */
  displayName: string | null;
  /**
   * SIL-1132: true iff the org owns at least one asset. Drives the
   * individual-org first-login routing (__root.tsx) — a fresh org with no
   * assets is routed to the Add Asset wizard instead of profile completion;
   * profile completion is required once the org's first asset exists (or,
   * independently, whenever the sell flow is attempted — gated separately by
   * canSell at the component level, e.g. ProfileLockedCard).
   */
  hasAssets: boolean;
  /**
   * 929: own-org nudge counts for one-time manual serial entry (individual
   * orgs). 0/null for anyone else or an API that predates 929 (fail closed:
   * no nudge is shown rather than a wrong one).
   */
  serialEntryPendingCount: number;
  serialEntryServerCount: number;
  serialEntryFirstAssetId: string | null;
}

let identityPromise: Promise<ClientIdentity> | null = null;

// Inlined (not imported from lib/api) to avoid a circular import — api.ts imports
// getIdToken indirectly via createAuthFetch(client). The deployed customer site
// has no same-origin /api proxy, so API calls must target the absolute VITE_API_BASE_URL.
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001';

async function loadClientIdentity(): Promise<ClientIdentity> {
  // Send the ID token — clientAuthMiddleware validates `aud`/`email`, which the
  // access token does not carry.
  //
  // Throw (rather than return an empty identity) on a transient first-load
  // failure — the token isn't yet stored right after the auth-callback
  // redirect, or /api/client/me flaked. fetchClientIdentity() leaves a thrown
  // load uncached so react-query retries; otherwise a seller gets pinned to an
  // empty (orgType: null) identity for the whole page session and the dashboard
  // mis-renders them as a buyer with no portfolio graphs.
  const idToken = getIdToken();
  if (!idToken) throw new Error('client identity: id token not available yet');
  const res = await fetch(`${API_BASE}/api/client/me`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  // `status` rides on the error (same shape as lib/api's throwIfNotOk) so
  // AuthProvider can tell a 401 - a valid Cognito token with no client_user
  // row - from a transient failure, and the shared query client stops
  // retrying it.
  if (!res.ok) {
    throw Object.assign(new Error(`client identity: HTTP ${res.status}`), { status: res.status });
  }
  const data = (await res.json()) as {
    orgType?: 'seller' | 'buyer';
    orgId?: string;
    orgName?: string | null;
    role?: 'admin' | 'member';
    email?: string;
    sellerProfile?: unknown;
    buyerProfile?: unknown;
    onboardingCompleted?: boolean;
    logoUrl?: string | null;
    teamId?: string | null;
    canSell?: boolean;
    canBuy?: boolean;
    profiles?: {
      seller?: { missingFields?: string[] };
      buyer?: { missingFields?: string[] };
    };
    organizationType?: 'business' | 'individual';
    individualProfileMissing?: string[];
    displayName?: string | null;
    tosAccepted?: boolean;
    hasAssets?: boolean;
    serialEntryPendingCount?: number;
    serialEntryServerCount?: number;
    serialEntryFirstAssetId?: string | null;
  };
  return {
    orgType: data.orgType ?? null,
    orgId: data.orgId ?? null,
    orgName: data.orgName ?? null,
    orgRole: data.role ?? null,
    email: data.email ?? null,
    hasSellerProfile: data.sellerProfile != null,
    hasBuyerProfile: data.buyerProfile != null,
    onboardingCompleted: data.onboardingCompleted ?? false,
    logoUrl: data.logoUrl ?? null,
    teamId: data.teamId ?? null,
    canSell: data.canSell ?? false,
    canBuy: data.canBuy ?? false,
    sellerMissingFields: data.profiles?.seller?.missingFields ?? [],
    buyerMissingFields: data.profiles?.buyer?.missingFields ?? [],
    organizationType: data.organizationType ?? null,
    individualProfileMissing: data.individualProfileMissing ?? [],
    displayName: data.displayName ?? null,
    // Fail closed: an old/short response with no tosAccepted key gates the
    // user to /tos rather than silently letting an unaccepted user through.
    tosAccepted: data.tosAccepted ?? false,
    // Fail closed: an old/short response with no hasAssets key routes a
    // first-login individual org to the Add Asset wizard (the correct
    // behavior for a genuinely-empty org anyway) rather than skipping it.
    hasAssets: data.hasAssets ?? false,
    serialEntryPendingCount: data.serialEntryPendingCount ?? 0,
    serialEntryServerCount: data.serialEntryServerCount ?? 0,
    serialEntryFirstAssetId: data.serialEntryFirstAssetId ?? null,
  };
}

/** Fetch the signed-in user's org identity from the DB, memoized per page session. */
export function fetchClientIdentity(): Promise<ClientIdentity> {
  if (!identityPromise) {
    identityPromise = loadClientIdentity().catch((err) => {
      // Never cache a failed load — clear the memo so the next call (a
      // react-query retry or a remount) can try again once the token/endpoint
      // is ready, instead of sticking on a transient first-load failure.
      identityPromise = null;
      throw err;
    });
  }
  return identityPromise;
}

/** Drop the memoized identity so the next fetchClientIdentity() re-fetches. */
export function clearClientIdentity(): void {
  identityPromise = null;
}
